import type {TranslationFunction} from 'components/translation/utils';
import {useAppOverrides} from 'contexts/AppOverridesContext';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {useCourseTheme} from 'course/contexts/ThemeContext';
import {doc, getFirestore} from 'firebase/firestore';
import {useEffect, useRef} from 'react';
import {useDocumentData} from 'react-firebase-hooks/firestore';
import Swal from 'sweetalert2';
import {reloadPage} from 'appReload';

/**
 * Offers a reload when the deployed version on the `global/webapp` document
 * changes while the page is open. Watches the shared `version` field, plus
 * the app's own field when `AppOverrides.versionField` names one. The
 * versions are set by hand; each change prompts once.
 *
 * @return Nothing visible; the prompt is a toast.
 * @throws Never.
 */
export const ApplicationVersionMonitor = () => {
  const {versionField} = useAppOverrides();
  const [appData] = useDocumentData(doc(getFirestore(), 'global/webapp'));
  const t = useTranslateFunction('miscUI');
  const {brandName} = useCourseTheme();
  // The versions the page loaded with, or null until the first snapshot.
  const seenVersionsRef = useRef<string | null>(null);

  useEffect(() => {
    if (!appData) {
      return;
    }
    const versions = JSON.stringify([
      appData.version ?? null,
      versionField ? (appData[versionField] ?? null) : null,
    ]);
    if (seenVersionsRef.current === null) {
      seenVersionsRef.current = versions;
      return;
    }
    if (versions === seenVersionsRef.current) {
      return;
    }
    seenVersionsRef.current = versions;
    suggestPageReload({t, brandName});
  }, [appData, versionField, t, brandName]);

  return null;
};

function suggestPageReload({
  t,
  brandName,
}: {
  t: TranslationFunction;
  brandName: string;
}) {
  void Swal.fire({
    title: t('{{brandName}} website updated', {
      substitutions: {brandName},
      forceString: true,
    }),
    position: 'top-end',
    confirmButtonText: t('Reload', {forceString: true}),
    cancelButtonText: t('Ignore', {forceString: true}),
    toast: true,
    showCancelButton: true,
    icon: 'info',
    preConfirm: () => {
      reloadPage({reason: 'version-update'});
    },
  });
}
